import React, {useEffect, useState} from "react";
import {Button, Modal} from "antd";
import useUserContext from "../../hooks/useUserContext";
import useToast from "../../hooks/useToast";
import {BigInput} from "../forms/bigInput/BigInput";
import {createManager} from "../../api/createManager";
import {ToastSkin} from "../../context/ToastContext";

interface NewShopManagerModalProps {
    isOpen: boolean;
    onClose: () => void;
    reloadManagers: () => void;
}

export interface ManagerFormValuesProps {
    name: string;
    email: string;
}

const DEFAULT_FORM_VALUES = { name: '', email: '' };

const NewShopManagerModal = ({
    isOpen,
    onClose,
    reloadManagers
}: NewShopManagerModalProps) => {
    const [formValues, setFormValues] = useState<ManagerFormValuesProps>(DEFAULT_FORM_VALUES);
    const [formErrors, setFormErrors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { selectedDomain } = useUserContext();
    const { showToast } = useToast();

    const onCloseModal = () => {
        setFormValues(DEFAULT_FORM_VALUES);
        setFormErrors([]);
        setIsLoading(false);
        onClose();
    }

    const isManagerValid = (formValues: ManagerFormValuesProps): boolean => {
        return formValues.name.length > 2 && formValues.email.length > 5 && formValues.email.includes('@') && formValues.email.includes('.');
    }

    const onCreateNewManager = async () => {
        setIsLoading(true);
        const isValid = isManagerValid(formValues);
        if(selectedDomain && isValid) {
            try {
                const response = await createManager(selectedDomain, formValues);
                if(response.data === 'ok') {
                    setIsLoading(false);
                    reloadManagers();
                    showToast({ skin: ToastSkin.approval, text: `New manager (${formValues.name}) was created successfully`});
                    onClose();
                } else {
                    showToast({text: 'An unknown error occurred while adding a new manager'});
                }
            } catch (error) {
                console.log("error: ", error);
                showToast({ text: 'An unknown error occurred while adding a new manager'});
            }
        } else {
            showToast({ text: 'Please make sure to type a valid name and email'});
        }
        setIsLoading(false);
        return false;
    }

    const onFormUpdate = (newFormValues: any) => {
        !formErrors.length && setFormErrors([]);
        setFormValues(newFormValues);
    }

    useEffect(() => {
        setFormValues(DEFAULT_FORM_VALUES);
    }, [isOpen]);

    return (
        <Modal
            open={isOpen}
            onOk={onCreateNewManager}
            onCancel={onCloseModal}
            confirmLoading={isLoading}
            okButtonProps={{ style: { display: 'none' }}}
            cancelButtonProps={{ style: { display: 'none' }}}
            width={500}
            destroyOnHidden={true}
        >
            {/*<NewNotificationForm onFormUpdate={onFormUpdate} formErrors={formErrors} isLoading={isLoading} />*/}
            <div className="new-manager-form-container">
                <div className="title">
                    Add New Manager
                </div>
                <div className="description">
                    This manager will be able to login to your shop Vuelve account, see all the data regarding that account. He/She will also be able to create, edit or delete templates, change shop settings and add/remove other managers.
                </div>
                <div className="field">
                    <BigInput value={formValues.name} setValue={(newVal => onFormUpdate({ ...formValues, name: newVal }))} className='name-input' placeholder="Jane Doe" description='Type the name of the new Manager' />
                </div>
                <div className="field">
                    <BigInput value={formValues.email} setValue={(newVal => onFormUpdate({ ...formValues, email: newVal }))} className='email-input' placeholder="jane@doe.com" description='Type the email of the new Manager' />
                </div>
                <div className="action-bar">
                    <Button size="large" onClick={onClose}>Cancel</Button>
                    <Button size="large" type="primary" onClick={onCreateNewManager}>Create New Manager</Button>
                </div>
            </div>
        </Modal>
    );
}

export default NewShopManagerModal;